<template>
  <div v-if="enabled" class="video-with-chapters-view">
    <div class="video-with-chapters-view__main-grid">
      <div class="video-with-chapters-view__player-column">
        <div class="video-with-chapters-view__player">
          <template v-if="usesIframeEmbed">
            <iframe
              :key="iframeKey"
              class="video-with-chapters-view__iframe"
              :src="iframePlaybackSrc"
              :title="videoAriaLabel"
              allow="
              accelerometer;
              autoplay;
              clipboard-write;
              encrypted-media;
              gyroscope;
              picture-in-picture;
              fullscreen;
            "
              allowfullscreen
            ></iframe>
            <img
              v-if="chapterFramePoster"
              class="video-with-chapters-view__chapter-frame"
              :src="chapterFramePoster"
              alt=""
            />
            <button
              v-if="chapterFramePoster && !isPlaying"
              type="button"
              class="video-with-chapters-view__play-overlay"
              :aria-label="playOverlayAriaLabel"
              @click="handleChapterPosterPlay"
            >
              <span class="video-with-chapters-view__play-overlay-icon" aria-hidden="true"></span>
            </button>
          </template>
          <video
            v-else
            ref="videoPlayer"
            class="video-with-chapters-view__video"
            :src="videoUrl"
            :poster="videoPosterUrl"
            :aria-label="videoAriaLabel"
            controls
            playsinline
            preload="metadata"
            @timeupdate="handleTimeUpdate"
            @play="onNativeVideoPlay"
            @pause="isPlaying = false"
            @ended="isPlaying = false"
          >
            Your browser does not support the video tag.
          </video>
        </div>

        <div v-if="showScrubBar" class="video-with-chapters-view__progress">
          <span class="video-with-chapters-view__time" aria-hidden="true">{{
            currentTimeLabel
          }}</span>
          <div
            ref="scrubBar"
            class="video-with-chapters-view__scrub-bar"
            role="slider"
            tabindex="0"
            aria-label="Video progress"
            aria-orientation="horizontal"
            :aria-valuemin="0"
            :aria-valuemax="scrubDuration"
            :aria-valuenow="Math.floor(displaySeconds)"
            :aria-valuetext="scrubValueText"
            @pointerdown="onScrubPointerDown"
            @keydown="onScrubKeydown"
          >
            <span
              v-for="segment in scrubSegments"
              :key="segment.key"
              class="video-with-chapters-view__scrub-segment"
              :style="segment.style"
            >
              <span class="video-with-chapters-view__scrub-fill" :style="segment.fillStyle"></span>
            </span>
            <div class="video-with-chapters-view__scrub-handle" :style="scrubHandleStyle"></div>
          </div>
          <span class="video-with-chapters-view__time" aria-hidden="true">{{ durationLabel }}</span>
        </div>
      </div>

      <!-- Chapters card -->
      <aside
        v-if="chapters && chapters.length"
        class="video-with-chapters-view__chapters-card"
        aria-label="Chapters"
      >
        <header class="video-with-chapters-view__chapters-header">
          <h2 class="video-with-chapters-view__chapters-title">Chapters</h2>
          <span class="video-with-chapters-view__chapters-count">{{ chapters.length }}</span>
        </header>

        <ol class="video-with-chapters-view__chapters">
          <li
            v-for="(chapter, index) in chapters"
            :key="chapterKey(chapter, index)"
            class="video-with-chapters-view__chapter"
          >
            <VideoChapterRow
              :chapter-number="chapter.chapterNumber"
              :title="chapter.title"
              :start-time-seconds="chapter.startTimeSeconds"
              :start-time-label="chapter.startTimeLabel"
              :description="chapter.description"
              :thumbnail-url="chapter.thumbnailUrl"
              :editable="editable"
              @seek="handleChapterClick(chapter)"
              @edit="openChapterEdit(index)"
            />
          </li>
        </ol>
      </aside>
    </div>

    <!-- Summary card -->
    <section
      v-if="summaryLead || (summaryBullets && summaryBullets.length)"
      class="video-with-chapters-view__summary"
      aria-label="Summary"
    >
      <div class="video-with-chapters-view__summary-header">
        <p class="video-with-chapters-view__summary-label">Summary</p>
        <button
          v-if="editable"
          type="button"
          class="video-with-chapters-view__summary-edit"
          aria-label="Edit summary"
          @click="openSummaryEdit"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="18"
            height="18"
            viewBox="0 0 24 24"
            fill="none"
            aria-hidden="true"
            focusable="false"
          >
            <path
              d="M2.87604 18.1156C2.92193 17.7025 2.94488 17.4959 3.00738 17.3027C3.06284 17.1313 3.14195 16.9684 3.24253 16.8187C3.35603 16.6498 3.50299 16.5028 3.79691 16.2089L17 3C18.1046 1.89543 19.8954 1.89543 21 3C22.1046 4.10457 22.1046 5.89543 21 7L7.79691 20.2031C7.50299 20.497 7.35603 20.644 7.18718 20.7575C7.03751 20.858 6.87456 20.9372 6.70314 20.9926C6.50993 21.0551 6.30337 21.0781 5.89025 21.124L2.5 21.5L2.87604 18.1156Z"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </svg>
        </button>
      </div>
      <p class="video-with-chapters-view__summary-lead">{{ summaryLead }}</p>
      <ul
        v-if="summaryBullets && summaryBullets.length"
        class="video-with-chapters-view__summary-bullets"
      >
        <li
          v-for="(bullet, index) in summaryBullets"
          :key="index"
          class="video-with-chapters-view__summary-bullet"
        >
          <span class="video-with-chapters-view__summary-marker" aria-hidden="true"></span>
          <span class="video-with-chapters-view__summary-text">{{ bullet }}</span>
        </li>
      </ul>
    </section>

    <template v-if="editable">
      <EditSummaryModal
        :modelValue="summaryModalOpen"
        :twoPart="true"
        :summaryLead="summaryLead"
        :summaryBullets="summaryBullets"
        :saving="summarySaving"
        @update:modelValue="summaryModalOpen = $event"
        @save="onSummarySave"
      />
      <EditStepModal
        :modelValue="chapterModalOpen"
        variant="chapter"
        :title="editingChapter.title"
        :description="editingChapter.description"
        :timestampRange="editingChapter.startTimeLabel"
        :saving="isEditingChapterSaving"
        @update:modelValue="chapterModalOpen = $event"
        @save="onChapterSave"
      />
    </template>
  </div>
</template>

<script setup>
import { computed, getCurrentInstance, onBeforeUnmount, onMounted, ref, watch } from "vue";
import { LD_FLAG_VIDEO_WITH_CHAPTERS_VIEW } from "@components/constants/launchDarklyFlagKeys";
import VideoChapterRow from "@components/VideoChapterRow/VideoChapterRow.vue";
import EditSummaryModal from "@components/EditSummaryModal/EditSummaryModal.vue";
import EditStepModal from "@components/EditStepModal/EditStepModal.vue";

const props = defineProps({
  /** Signed Cloudflare Stream playback URL. */
  videoUrl: {
    type: String,
    required: true,
  },
  /** Total video duration in seconds; drives the scrub bar and its time labels. */
  durationSeconds: {
    type: Number,
    default: 0,
  },
  /** Ordered list of chapters, each matching VideoChapterRow props. */
  chapters: {
    type: Array,
    default: () => [],
  },
  /** IQ-generated lead sentence, e.g. "What you'll learn: …". */
  summaryLead: {
    type: String,
    default: "",
  },
  /** IQ-generated bullet points; rendered with decorative markers. */
  summaryBullets: {
    type: Array,
    default: () => [],
  },
  /** Playhead position in seconds; seeks the player when controlled externally. Never highlights a chapter row. */
  currentTimeSeconds: {
    type: Number,
    default: 0,
  },
  /** Shows the summary and chapter edit pencils and hosts their dialogs. Learner views leave this off. */
  editable: {
    type: Boolean,
    default: false,
  },
  /** Save in flight for the summary dialog. */
  summarySaving: {
    type: Boolean,
    default: false,
  },
  /** Index of the chapter being saved; -1 when none. */
  chapterSavingIndex: {
    type: Number,
    default: -1,
  },
});

const emit = defineEmits(["chapter-click", "seek", "summary-save", "chapter-save"]);

const app = getCurrentInstance();
const { proxy } = app || {};

const enabled = ref(true);
const videoPlayer = ref(null);
const playheadSeconds = ref(0);
const isPlaying = ref(false);
const iframeKey = ref(0);
const iframeAutoplay = ref(false);
/** When set, iframe uses this signed/jump URL instead of mutating the base playback URL. */
const activeIframeSrc = ref(null);
/** Cloudflare chapter thumbnail shown until the iframe loads at the chapter timestamp. */
const chapterFramePoster = ref("");
const activeVideoPoster = ref("");
const activeChapter = ref(null);
const summaryModalOpen = ref(false);
const chapterModalOpen = ref(false);
const editingChapterIndex = ref(-1);
const scrubBar = ref(null);
const isScrubbing = ref(false);
const scrubSeconds = ref(0);

const SCRUB_KEY_STEP_SECONDS = 5;

const editingChapter = computed(
  () =>
    props.chapters?.[editingChapterIndex.value] ?? {
      title: "",
      description: "",
      startTimeLabel: "",
    }
);
const isEditingChapterSaving = computed(
  () => editingChapterIndex.value >= 0 && props.chapterSavingIndex === editingChapterIndex.value
);

function isCloudflareStreamUrl(value) {
  if (!value || typeof value !== "string") return false;
  return value.includes("cloudflarestream.com") || value.includes("videodelivery.net");
}

function normalizePlaybackUrl(value) {
  const raw = String(value ?? "").trim();
  if (!raw) return "";
  if (raw.includes("<iframe")) {
    const match = raw.match(/src=["']([^"']+)["']/);
    return match?.[1] ?? "";
  }
  return raw;
}

function extractCloudflareStreamUid(url) {
  const normalized = normalizePlaybackUrl(url);
  if (!normalized) return "";
  try {
    const segments = new URL(normalized).pathname.split("/").filter(Boolean);
    const uid = segments.find((segment) => segment !== "iframe" && /^[a-f0-9]{32}$/i.test(segment));
    return uid ?? "";
  } catch {
    const match = normalized.match(/\/([a-f0-9]{32})\//i);
    return match?.[1] ?? "";
  }
}

function buildStreamIframeSrc(baseUrl, { startSeconds = 0, autoplay = false } = {}) {
  const normalized = normalizePlaybackUrl(baseUrl);
  if (!normalized) return "";
  try {
    const parsed = new URL(normalized);
    parsed.searchParams.delete("startTime");
    parsed.searchParams.delete("autoplay");
    if (startSeconds > 0) {
      parsed.searchParams.set("startTime", String(Math.floor(startSeconds)));
    }
    if (autoplay) {
      parsed.searchParams.set("autoplay", "true");
    }
    return parsed.toString();
  } catch {
    let url = normalized.replace(/([?&])startTime=\d+/g, "").replace(/([?&])autoplay=[^&]*/g, "");
    const separator = url.includes("?") ? "&" : "?";
    if (startSeconds > 0) {
      url = `${url}${separator}startTime=${Math.floor(startSeconds)}`;
    }
    if (autoplay) {
      url = `${url}${url.includes("?") ? "&" : "?"}autoplay=true`;
    }
    return url;
  }
}

function sanitizeIframePlaybackUrl(url, { autoplay = false } = {}) {
  const normalized = normalizePlaybackUrl(url);
  if (!normalized) return "";
  try {
    const parsed = new URL(normalized);
    parsed.searchParams.delete("autoplay");
    if (autoplay) {
      parsed.searchParams.set("autoplay", "true");
    }
    return parsed.toString();
  } catch {
    const withoutAutoplay = normalized.replace(/([?&])autoplay=[^&]*/g, "");
    if (!autoplay) {
      return withoutAutoplay;
    }
    return `${withoutAutoplay}${withoutAutoplay.includes("?") ? "&" : "?"}autoplay=true`;
  }
}

function resolveChapterIframeSrc(chapter, startSeconds, { autoplay = false } = {}) {
  const jumpUrl = String(chapter?.videoJumpUrl ?? "").trim();
  if (jumpUrl) {
    return sanitizeIframePlaybackUrl(jumpUrl, { autoplay });
  }
  return buildStreamIframeSrc(props.videoUrl, { startSeconds, autoplay });
}

const usesIframeEmbed = computed(() => {
  const url = props.videoUrl;
  if (!url) return false;
  if (url.includes("<iframe")) return true;
  if (url.includes("embed.api.video") || url.includes("api.video/vod")) return true;
  return isCloudflareStreamUrl(url);
});

const iframePlaybackSrc = computed(() => {
  if (activeIframeSrc.value) {
    return activeIframeSrc.value;
  }
  return buildStreamIframeSrc(props.videoUrl, {
    startSeconds: playheadSeconds.value,
    autoplay: iframeAutoplay.value,
  });
});

const videoPosterUrl = computed(() => {
  if (activeVideoPoster.value) {
    return activeVideoPoster.value;
  }
  const first = props.chapters?.[0];
  const poster = first?.thumbnailUrl ?? first?.thumbnail ?? "";
  return String(poster ?? "").trim();
});

const videoAriaLabel = computed(() => `Video microlearning player`);
const playOverlayAriaLabel = computed(() => "Play video from this chapter");

function chapterKey(chapter, index) {
  return `${chapter.chapterNumber}-${chapter.startTimeSeconds}-${index}`;
}

const scrubDuration = computed(() => Math.max(Number(props.durationSeconds) || 0, 0));
const showScrubBar = computed(() => scrubDuration.value > 0);

const displaySeconds = computed(() =>
  Math.min(isScrubbing.value ? scrubSeconds.value : playheadSeconds.value, scrubDuration.value)
);

const scrubSegments = computed(() => {
  const total = scrubDuration.value;
  const starts = (props.chapters || [])
    .map((chapter) => Number(chapter?.startTimeSeconds))
    .filter((start) => Number.isFinite(start) && start > 0 && start < total)
    .sort((a, b) => a - b);
  const boundaries = [0, ...new Set(starts), total];
  const time = displaySeconds.value;

  return boundaries.slice(0, -1).map((start, index) => {
    const end = boundaries[index + 1];
    const played = Math.min(Math.max((time - start) / (end - start), 0), 1);
    return {
      key: `${start}-${end}`,
      style: { flex: `${end - start} 1 0` },
      fillStyle: { width: `${played * 100}%` },
    };
  });
});

const scrubHandleStyle = computed(() => ({
  left: `${(displaySeconds.value / scrubDuration.value) * 100}%`,
}));

function formatClock(seconds) {
  const total = Math.max(Math.floor(Number(seconds) || 0), 0);
  const hours = Math.floor(total / 3600);
  const minutes = Math.floor((total % 3600) / 60);
  const secs = String(total % 60).padStart(2, "0");
  return hours > 0 ? `${hours}:${String(minutes).padStart(2, "0")}:${secs}` : `${minutes}:${secs}`;
}

const currentTimeLabel = computed(() => formatClock(displaySeconds.value));
const durationLabel = computed(() => formatClock(scrubDuration.value));
const scrubValueText = computed(() => `${currentTimeLabel.value} of ${durationLabel.value}`);

function secondsFromPointer(event) {
  const rect = scrubBar.value?.getBoundingClientRect?.();
  if (!rect || !rect.width) return scrubSeconds.value;
  const ratio = Math.min(Math.max((event.clientX - rect.left) / rect.width, 0), 1);
  return ratio * scrubDuration.value;
}

function onScrubPointerMove(event) {
  scrubSeconds.value = secondsFromPointer(event);
}

function stopScrubbing() {
  window.removeEventListener("pointermove", onScrubPointerMove);
  window.removeEventListener("pointerup", onScrubPointerUp);
  window.removeEventListener("pointercancel", stopScrubbing);
  isScrubbing.value = false;
}

function onScrubPointerUp(event) {
  const target = secondsFromPointer(event);
  stopScrubbing();
  seekTo(target);
}

function onScrubPointerDown(event) {
  if (event.button) return;
  event.preventDefault();
  scrubBar.value?.focus?.();
  scrubSeconds.value = secondsFromPointer(event);
  isScrubbing.value = true;
  window.addEventListener("pointermove", onScrubPointerMove);
  window.addEventListener("pointerup", onScrubPointerUp);
  window.addEventListener("pointercancel", stopScrubbing);
}

function onScrubKeydown(event) {
  const current = playheadSeconds.value;
  const targets = {
    ArrowRight: current + SCRUB_KEY_STEP_SECONDS,
    ArrowUp: current + SCRUB_KEY_STEP_SECONDS,
    ArrowLeft: current - SCRUB_KEY_STEP_SECONDS,
    ArrowDown: current - SCRUB_KEY_STEP_SECONDS,
    Home: 0,
    End: scrubDuration.value,
  };
  if (!(event.key in targets)) return;
  event.preventDefault();
  seekTo(targets[event.key]);
}

function syncVideoTime(seconds) {
  const video = videoPlayer.value;
  if (video && typeof seconds === "number" && Number.isFinite(seconds)) {
    try {
      video.currentTime = seconds;
    } catch (e) {
      // Setting currentTime can throw in some embed contexts.
    }
  }
}

function handleTimeUpdate(event) {
  const time = event?.target?.currentTime;
  if (typeof time === "number" && Number.isFinite(time)) {
    playheadSeconds.value = time;
  }
}

function onNativeVideoPlay() {
  isPlaying.value = true;
  activeVideoPoster.value = "";
}

function reloadIframePlayback({ autoplay = false } = {}) {
  iframeAutoplay.value = autoplay;
  iframeKey.value += 1;
}

function seekTo(seconds) {
  const target = Math.min(
    Math.max(Number(seconds) || 0, 0),
    props.durationSeconds || Number(seconds) || 0
  );
  playheadSeconds.value = target;
  if (usesIframeEmbed.value) {
    activeIframeSrc.value = null;
    chapterFramePoster.value = "";
    isPlaying.value = true;
    reloadIframePlayback({ autoplay: true });
  } else {
    syncVideoTime(target);
  }
  emit("seek", target);
}

function handleChapterClick(chapter) {
  const target = Number(chapter?.startTimeSeconds) || 0;

  if (usesIframeEmbed.value) {
    playheadSeconds.value = target;
    isPlaying.value = true;
    iframeAutoplay.value = true;
    activeChapter.value = chapter;
    chapterFramePoster.value = "";
    activeIframeSrc.value = resolveChapterIframeSrc(chapter, target, { autoplay: true });
    iframeKey.value += 1;
    emit("seek", target);
    emit("chapter-click", chapter);
    return;
  }

  activeVideoPoster.value = "";
  seekTo(target);
  const video = videoPlayer.value;
  if (video && typeof video.play === "function") {
    try {
      const playPromise = video.play();
      if (playPromise && typeof playPromise.catch === "function") {
        playPromise.catch(() => {});
      }
    } catch (e) {
      // play() can throw in jsdom / embedded contexts; seek still applies.
    }
  }
  isPlaying.value = true;
  emit("chapter-click", chapter);
}

function openSummaryEdit() {
  summaryModalOpen.value = true;
}

function openChapterEdit(index) {
  editingChapterIndex.value = index;
  chapterModalOpen.value = true;
}

function onSummarySave(payload) {
  emit("summary-save", {
    summary_lead: payload?.summary_lead,
    summary_bullets: payload?.summary_bullets,
    complete: (err) => {
      if (!err) summaryModalOpen.value = false;
    },
  });
}

function onChapterSave(payload) {
  emit("chapter-save", {
    index: editingChapterIndex.value,
    title: payload?.title,
    description: payload?.description,
    complete: (err) => {
      if (!err) chapterModalOpen.value = false;
    },
  });
}

function handleChapterPosterPlay() {
  const chapter = activeChapter.value;
  const target = Number(chapter?.startTimeSeconds) || playheadSeconds.value;
  isPlaying.value = true;
  iframeAutoplay.value = true;
  chapterFramePoster.value = "";
  activeIframeSrc.value = chapter
    ? resolveChapterIframeSrc(chapter, target, { autoplay: true })
    : buildStreamIframeSrc(props.videoUrl, {
        startSeconds: target,
        autoplay: true,
      });
  iframeKey.value += 1;
}

watch(
  () => props.currentTimeSeconds,
  (value) => {
    if (typeof value === "number" && Number.isFinite(value)) {
      playheadSeconds.value = value;
      if (!usesIframeEmbed.value) {
        syncVideoTime(value);
      }
    }
  },
  { immediate: true }
);

watch(
  () => props.videoUrl,
  (next, prev) => {
    const prevNormalized = normalizePlaybackUrl(prev);
    const nextNormalized = normalizePlaybackUrl(next);
    const hasChapterPreview = Boolean(activeIframeSrc.value || chapterFramePoster.value);

    // Signed playback URL arrived after the user picked a chapter — keep their preview.
    if (!prevNormalized && nextNormalized && hasChapterPreview) {
      iframeKey.value += 1;
      return;
    }

    const nextUid = extractCloudflareStreamUid(next);
    const prevUid = extractCloudflareStreamUid(prev);
    if (nextUid && prevUid && nextUid === prevUid && hasChapterPreview) {
      iframeKey.value += 1;
      return;
    }

    playheadSeconds.value = 0;
    isPlaying.value = false;
    iframeAutoplay.value = false;
    activeIframeSrc.value = null;
    chapterFramePoster.value = "";
    activeVideoPoster.value = "";
    activeChapter.value = null;
    iframeKey.value += 1;
  }
);

onBeforeUnmount(stopScrubbing);

onMounted(async () => {
  try {
    enabled.value = (await proxy?.$getFlag?.(LD_FLAG_VIDEO_WITH_CHAPTERS_VIEW, true)) === true;
  } catch {
    enabled.value = true;
  }
});
</script>

<style scoped>
.video-with-chapters-view {
  display: flex;
  flex-direction: column;
  gap: 11px;
  width: 100%;
  box-sizing: border-box;
  font-family: Inter, sans-serif;
}

.video-with-chapters-view__main-grid {
  display: flex;
  gap: 20px;
  align-items: flex-start;
  width: 100%;
  min-width: 0;
  container-type: inline-size;
}

.video-with-chapters-view__player-column {
  display: flex;
  flex: 1.57 1 0;
  flex-direction: column;
  gap: 8px;
  min-width: 0;
  width: 100%;
}

.video-with-chapters-view__player {
  position: relative;
  width: 100%;
  aspect-ratio: 16 / 9;
  border-radius: 8px;
  overflow: hidden;
  background-color: #101828;
}

.video-with-chapters-view__video,
.video-with-chapters-view__iframe {
  position: absolute;
  inset: 0;
  display: block;
  width: 100%;
  height: 100%;
  border: none;
  background-color: #101828;
}

.video-with-chapters-view__video {
  object-fit: cover;
}

.video-with-chapters-view__chapter-frame {
  position: absolute;
  inset: 0;
  z-index: 1;
  display: block;
  width: 100%;
  height: 100%;
  object-fit: contain;
  background-color: #101828;
  pointer-events: none;
}

.video-with-chapters-view__play-overlay {
  position: absolute;
  left: 50%;
  top: 50%;
  z-index: 2;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 64px;
  height: 64px;
  padding: 0;
  border: none;
  border-radius: 50%;
  background: rgba(0, 0, 0, 0.55);
  transform: translate(-50%, -50%);
  cursor: pointer;
}

.video-with-chapters-view__play-overlay:focus-visible {
  outline: 2px solid #ffffff;
  outline-offset: 3px;
}

.video-with-chapters-view__play-overlay-icon {
  display: block;
  width: 0;
  height: 0;
  margin-left: 4px;
  border-style: solid;
  border-width: 12px 0 12px 20px;
  border-color: transparent transparent transparent #ffffff;
}

.video-with-chapters-view__progress {
  display: flex;
  align-items: center;
  gap: 10px;
  height: 20px;
}

.video-with-chapters-view__time {
  flex-shrink: 0;
  min-width: 34px;
  color: #667085;
  font-size: 12px;
  font-weight: 600;
  line-height: 14.52px;
  font-variant-numeric: tabular-nums;
}

.video-with-chapters-view__time:last-child {
  text-align: right;
}

.video-with-chapters-view__scrub-bar {
  position: relative;
  display: flex;
  flex: 1;
  align-items: center;
  height: 20px;
  min-width: 0;
  border-radius: 4px;
  cursor: pointer;
  touch-action: none;
}

.video-with-chapters-view__scrub-bar:focus-visible {
  outline: 2px solid #7f56d9;
  outline-offset: 2px;
}

.video-with-chapters-view__scrub-segment {
  position: relative;
  min-width: 2px;
  height: 6px;
  background-color: #eaecf0;
  overflow: hidden;
}

.video-with-chapters-view__scrub-segment:first-child {
  border-radius: 3px 0 0 3px;
}

.video-with-chapters-view__scrub-segment:last-of-type {
  border-radius: 0 3px 3px 0;
}

.video-with-chapters-view__scrub-segment:only-of-type {
  border-radius: 3px;
}

.video-with-chapters-view__scrub-segment:not(:last-of-type)::after {
  content: "";
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  width: 2px;
  background-color: #ffffff;
}

.video-with-chapters-view__scrub-fill {
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  background-color: #7f56d9;
}

.video-with-chapters-view__scrub-handle {
  position: absolute;
  top: 50%;
  width: 14px;
  height: 14px;
  margin: -7px 0 0 -7px;
  box-sizing: border-box;
  border: 2px solid #7f56d9;
  border-radius: 50%;
  background-color: #ffffff;
  box-shadow: 0 1px 2px rgba(16, 24, 40, 0.15);
  pointer-events: none;
}

.video-with-chapters-view__chapters-card {
  display: flex;
  flex-direction: column;
  flex: 1 1 0;
  min-width: 0;
  max-width: 413px;
  /* Match the video column height (16:9 video + 8px gap + 20px scrub row) so the list scrolls inside the card. */
  max-height: calc((100cqi - 20px) * 1.57 / 2.57 * 9 / 16 + 28px);
  border: 1px solid #eaecf0;
  border-radius: 12px;
  background-color: #ffffff;
  box-shadow: 0 1px 2px rgba(16, 24, 40, 0.05);
  overflow: hidden;
}

.video-with-chapters-view__chapters-header {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 16px 18px;
  border-bottom: 1px solid #eaecf0;
  flex-shrink: 0;
}

.video-with-chapters-view__chapters-title {
  margin: 0;
  font-size: 15px;
  font-weight: 600;
  line-height: normal;
  color: #101828;
}

.video-with-chapters-view__chapters-count {
  margin-left: auto;
  padding: 2px 9px;
  border-radius: 999px;
  background-color: #f2f4f7;
  font-size: 12px;
  font-weight: 600;
  line-height: normal;
  color: #667085;
}

.video-with-chapters-view__chapters {
  list-style: none;
  margin: 0;
  padding: 4px 8px 8px;
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  scrollbar-width: thin;
  scrollbar-color: #aaafba #eaecf0;
}

.video-with-chapters-view__chapters::-webkit-scrollbar {
  width: 4px;
}

.video-with-chapters-view__chapters::-webkit-scrollbar-track {
  background: #eaecf0;
  border-radius: 2px;
}

.video-with-chapters-view__chapters::-webkit-scrollbar-thumb {
  background: #aaafba;
  border-radius: 2px;
}

.video-with-chapters-view__chapter {
  /* Keep the row's edit pencil aligned with the padding applied to the row below. */
  --video-chapter-row-pad-x: 10px;
  --video-chapter-row-pad-y: 12px;
  margin: 0;
  padding: 0;
}

.video-with-chapters-view__chapter :deep(.video-chapter-row) {
  border: none;
  border-radius: 8px;
  padding: 12px 10px;
  background: transparent;
}

.video-with-chapters-view__chapter :deep(.video-chapter-row:hover),
.video-with-chapters-view__chapter :deep(.video-chapter-row--hover) {
  background-color: #f9fafb;
}

.video-with-chapters-view__summary {
  border: 1px solid #eaecf0;
  border-radius: 12px;
  background-color: #ffffff;
  padding: 20px 22px;
  box-shadow: 0 1px 2px rgba(16, 24, 40, 0.05);
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.video-with-chapters-view__summary-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}

.video-with-chapters-view__summary-edit {
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  /* Offset the 32px hit target so the header keeps the label's height. */
  margin: -8px -8px -8px 0;
  padding: 0;
  border: none;
  border-radius: 8px;
  background: transparent;
  color: #667085;
  cursor: pointer;
  transition: background-color 0.15s ease;
}

.video-with-chapters-view__summary-edit:hover {
  background-color: #f2f4f7;
}

.video-with-chapters-view__summary-edit:focus-visible {
  outline: 2px solid #7f56d9;
  outline-offset: 2px;
}

.video-with-chapters-view__summary-label {
  margin: 0;
  font-size: 13px;
  font-weight: 600;
  line-height: normal;
  letter-spacing: 0.26px;
  text-transform: uppercase;
  color: #667085;
}

.video-with-chapters-view__summary-lead {
  color: #101828;
  font-size: 14.5px;
  font-weight: 600;
  line-height: normal;
  margin: 0;
}

.video-with-chapters-view__summary-bullets {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.video-with-chapters-view__summary-bullet {
  display: flex;
  align-items: flex-start;
  gap: 10px;
}

.video-with-chapters-view__summary-marker {
  flex-shrink: 0;
  width: 15px;
  height: 15px;
  margin-top: 2px;
  border-radius: 50%;
  background: radial-gradient(circle at 30% 30%, #f4ebff 0%, #e9d7fe 55%, #d6bbfb 100%);
  box-shadow: inset 0 0 0 1px rgba(105, 65, 198, 0.15);
}

.video-with-chapters-view__summary-text {
  color: #475167;
  font-size: 13.5px;
  font-weight: 400;
  line-height: normal;
}

@media (max-width: 960px) {
  .video-with-chapters-view__main-grid {
    flex-direction: column;
  }

  .video-with-chapters-view__chapters-card {
    max-width: none;
    width: 100%;
  }

  .video-with-chapters-view__chapters {
    max-height: 320px;
  }
}
</style>
